import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { usePeople, type Person } from '../api/use-people.js';
import { PersonLessons } from '../components/person-lessons.js';
import { ChildSignature } from '../components/child-signature.js';
import { PracticesHomeRow } from '../components/practices-home-row.js';
import { ChildTeams } from '../components/child-teams.js';
import { Disclosure } from '../components/disclosure.js';
import { day } from '../components/video-format.js';
import { ChildConsent } from '../components/child-consent.js';
import { ChildSignIn } from '../components/child-sign-in.js';
import { FinishAthlete } from '../components/finish-athlete.js';
import { ChildFacts } from '../components/child-facts.js';
import { useMeContext } from '../api/me-provider.js';
import { WaitNote } from '../components/wait-note.js';

/**
 * One person — the account holder or someone they are responsible for.
 *
 * ONE ROUTE, TWO ENTRANCES. The same component draws both, because an adult is
 * a person in the system like any other. The only difference is what leads:
 * a child's page will lead with the waiver, since that is the one thing
 * preventing a booking, and yours leads with details because nothing is
 * blocking.
 *
 * It reads the account's people rather than a route of its own. There is
 * nothing here the list does not already carry, and a second route would
 * return the same fields and drift from them the first time one gained a field.
 *
 * THEIR LESSONS LEAD (mockup of 11 October, which overturned the 24 September
 * ruling that a lesson is reached only from the Lessons tab). Each athlete has
 * their own lesson record now, so "Noa's lessons" is a real list rather than a
 * filter of bookings — each row opens Noa's page for that lesson, and back
 * comes here. Nothing is said when there are none.
 *
 * A CHILD'S CONSENTS follow (ruled 13 October): when the waiver was signed,
 * and the photo and video box, changeable here. Not on the holder's page — the
 * adult's own consent is not built yet.
 *
 * THEIR OWN SIGN-IN comes next (ruled 14-16 October): never asked, link sent,
 * on, or off, in child-sign-in.tsx.
 *
 * THE SECOND PARENT (canvas row D, ruled 17 October) sees the same page with
 * the facts in place of the controls: finishing a child, the waiver, photo and
 * video and the child's access are the first parent's (child-facts.tsx).
 *
 * A CHILD'S PAGE, REORDERED (Performance Signature stage 4b, plan approved 25
 * October; mockup *Signature in the Portal*, parent row): an unsigned waiver
 * or an unfinished child stays open at the top; then next and latest lesson
 * with All lessons; then the Performance Signature; then, closed under More,
 * consent, sign-in and details - each the same controls as before, one tap
 * down. The holder's own page is unchanged.
 *
 * BACK, LIKE EVERY SCREEN (Matthew, 25 October): the chevron alone, opening a
 * fixed page - never a step back through history. A history step here and a
 * fixed ‹ on the section pages sent child -> Motor Profile -> child -> Motor
 * Profile for ever. Where the parent came from rides in ?from=home: Home's
 * rows add it, the section pages keep it, and ‹ opens Home with it, Account
 * without.
 *
 * STRAIGHT FROM ACTIVATING (canvas row F, 26 October), the one-time password
 * arrives with the page, so "own sign-in" is open and the password in view.
 * Read once: the section clears the navigation, and the row stays as drawn.
 *
 * NOT HERE, AND NOT PRETENDED: permissions and sport - neither exists.
 * Messages live in their own tab.
 */
export function PersonRoute() {
  const { clientId } = useParams();
  const { state, reload } = usePeople();
  const navigate = useNavigate();
  const { state: meState, reload: reloadMe } = useMeContext();
  const second = meState.status === 'ready' && meState.me.profile?.role === 'second';
  const [search] = useSearchParams();
  const fromHome = search.get('from') === 'home';
  const location = useLocation();
  const [arrivedWithPassword] = useState(
    () => Boolean((location.state as { oneTimePassword?: string } | null)?.oneTimePassword),
  );

  const held = Boolean((location.state as { waiverHeld?: boolean } | null)?.waiverHeld);

  if (state.status === 'loading') {
    return (
      <section className="panel">
        <WaitNote className="empty">Checking…</WaitNote>
      </section>
    );
  }

  if (state.status === 'failed') {
    return (
      <section className="panel">
        <p className="empty">{state.message}</p>
        <button type="button" onClick={reload}>
          Try again
        </button>
      </section>
    );
  }

  if (state.status !== 'ready') {
    return (
      <section className="panel">
        <p className="empty">Not signed in.</p>
      </section>
    );
  }

  const person = state.people.find((candidate) => candidate.client_id === clientId);

  if (!person) {
    // Reachable by editing the URL, or by opening a stale link to somebody who
    // has since been removed. Says so plainly rather than rendering an empty
    // page that looks like a fault.
    return (
      <section className="panel">
        <h2>Not on this account</h2>
        <p className="empty">Nobody with that link is on your account.</p>
        <Link className="primary" to="/account">
          Back to your account
        </Link>
      </section>
    );
  }

  if (!person.is_account_holder) {
    const blocked = !second && (person.date_of_birth === null || !person.waiver_current);
    return (
      <>
        <div className="page-head">
          <Link className="back" to={fromHome ? '/' : '/account'} aria-label={fromHome ? 'Back to Home' : 'Back to your account'}>
            ‹
          </Link>
          <div>
            <h2>
              {person.first_name} {person.last_name}
            </h2>
            <span className="detail">Athlete</span>
          </div>
        </div>

        {/* An athlete a coach added is finished first, before anything else here (stage C). */}
        {/* The first time through (Matthew, 28 October): once every child is
            finished the parent lands on Home, not here; the next child to
            finish comes first. A waiver signed before and since updated is
            signed here and the page stays. */}
        {blocked ? (
          <FinishAthlete
            person={person}
            held={held}
            onDone={() => {
              reloadMe();
              if (person.waiver_signed_at !== null) return reload();
              const next = state.people.find(
                (other) =>
                  !other.is_account_holder &&
                  other.client_id !== person.client_id &&
                  (other.date_of_birth === null || !other.waiver_current),
              );
              reload();
              navigate(next ? `/account/people/${next.client_id}` : '/', { replace: true });
            }}
          />
        ) : null}
        {person.date_of_birth === null && !second ? null : (
          <>
            <PersonLessons clientId={person.client_id} condensed />
            <ChildSignature person={person} fromHome={fromHome} />
            {/* Practices (decision 6, ruled 1 November). */}
            <PracticesHomeRow athleteClientId={person.client_id} to={`/account/people/${person.client_id}/practices`} />
            {/* My Team, then the switch (Teams stage 3, board 8, approved 25 October). */}
            <ChildTeams person={person} />
            <span className="section-label">More</span>
            {second ? (
              <Disclosure title="Consent" summary={consentSummary(person)}>
                <ChildFacts person={person} />
              </Disclosure>
            ) : (
              <>
                {/* Unsigned, the waiver and Photo and video are the block at the top. */}
                {blocked ? null : (
                  <Disclosure title="Consent" summary={consentSummary(person)}>
                    <ChildConsent person={person} />
                  </Disclosure>
                )}
                <Disclosure
                  title={`${person.first_name}'s own sign-in`}
                  summary={signInSummary(person)}
                  open={arrivedWithPassword}
                >
                  <ChildSignIn person={person} />
                </Disclosure>
              </>
            )}
            <Disclosure title="Details" summary={person.date_of_birth ?? ''}>
              <Details person={person} />
            </Disclosure>
          </>
        )}
      </>
    );
  }

  // An adult who is the athlete signs their own waiver here first (W1).
  const ownWaiverDue = person.plays && !person.waiver_current;

  return (
    <>
      <div className="page-head">
        <Link className="back" to="/account" aria-label="Back to your account">
          ‹
        </Link>
        <div>
          <h2>
            {person.first_name} {person.last_name}
          </h2>
          <span className="detail">Account holder</span>
        </div>
      </div>
      {ownWaiverDue ? (
        <FinishAthlete
          person={person}
          held={held}
          onDone={() => {
            reloadMe();
            reload();
            navigate('/', { replace: true });
          }}
        />
      ) : null}
      <PersonLessons clientId={person.client_id} condensed={false} />
      {person.plays && !ownWaiverDue ? (
        <>
          <span className="section-label">More</span>
          <Disclosure title="Consent" summary={consentSummary(person)}>
            <ChildFacts person={person} />
          </Disclosure>
        </>
      ) : null}
      <span className="section-label">Details</span>
      <Details person={person} />
    </>
  );
}

function Details({ person }: { person: Person }) {
  return (
    <>
      <Fact label="First name" value={person.first_name} />
      <Fact label="Last name" value={person.last_name} />
      <Fact label="Date of birth" value={person.date_of_birth ?? ''} />
      {person.country ? <Fact label="Country" value={person.country} /> : null}
    </>
  );
}

/** The closed Consent row, in the words child-facts.tsx uses. */
function consentSummary(person: Person): string {
  const waiver = person.waiver_signed_at ? `Waiver approved ${day(person.waiver_signed_at)}` : 'Waiver not signed';
  if (person.image_video_allowed === null) return waiver;
  return `${waiver} · Photo and video: ${person.image_video_allowed ? 'Yes' : 'No'}`;
}

/** The closed sign-in row, in child-sign-in.tsx's states; nothing while never asked. */
function signInSummary(person: Person): string {
  const signIn = person.sign_in;
  if (!signIn) return '';
  if (signIn.enabled) return `Activated · ${signIn.username ?? ''}`;
  if (signIn.turned_off_at) return `Deactivated · ${signIn.username ?? ''}`;
  if (signIn.link_until) return 'Check your email';
  return '';
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="fact">
      <span className="label">{label}</span>
      <span className="value">{value}</span>
    </div>
  );
}
